import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Trash2, Check, X, Settings2, Users, Shield, Key, Eye, EyeOff, UserPlus, RefreshCw, CalendarDays, Download, AlertTriangle, BookOpen, FileText as FileTextIcon, Upload, BadgeCheck, Bell, ShieldAlert, FileBarChart, Banknote, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import LeaveRulesTab from '../components/settings/LeaveRulesTab';
import ViewWindowsTab from '../components/settings/ViewWindowsTab';
import EscSettingsTab from '../components/settings/EscSettingsTab';
import OperationsSettingsTab from '../components/settings/OperationsSettingsTab';
import DataHygieneTab from '../components/settings/DataHygieneTab';
import HRReportSettingsTab from '../components/settings/HRReportSettingsTab';
import WpsSettingsTab from '../components/settings/WpsSettingsTab';
import ExclusionsTab from '../components/settings/ExclusionsTab';
import ScheduledReportsTab from '../components/settings/ScheduledReportsTab';
import MenuOrderTab from '../components/settings/MenuOrderTab';
import CompanyProfileCard from '../components/settings/CompanyProfileCard';
import { ModuleAccessPicker } from '../components/settings/ModuleAccessPicker';
import { WelcomeBackSettingsCard } from '../components/settings/WelcomeBackSettingsCard';
import { NAV_ITEMS } from '../components/Layout';
import { presetForRole } from '../lib/moduleAccess';

const ALL_HREFS = NAV_ITEMS.map((n) => n.href);

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-800 border-red-200',
  admin: 'bg-purple-100 text-purple-800 border-purple-200',
  hr_manager: 'bg-blue-100 text-blue-800 border-blue-200',
  payroll_manager: 'bg-green-100 text-green-800 border-green-200',
  viewer: 'bg-gray-100 text-gray-800 border-gray-200',
  report_viewer: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  hr_manager: 'HR Manager',
  payroll_manager: 'Payroll Manager',
  viewer: 'Viewer',
  report_viewer: 'Report Viewer',
};

// Static roles as fallback
const STATIC_ROLES = {
  super_admin: { level: 100, description: 'Full access to all features' },
  admin: { level: 80, description: 'Full access except user management' },
  hr_manager: { level: 60, description: 'Manage employees, leaves, attendance, KPI' },
  payroll_manager: { level: 50, description: 'Manage attendance and payroll only' },
  viewer: { level: 10, description: 'View-only access to all data' },
  report_viewer: { level: 5, description: 'View reports only' },
};

const SettingsPage = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(STATIC_ROLES);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '' });
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'viewer',
    email: '',
    is_active: true,
    custom_access: true,
    module_access: presetForRole('viewer', ALL_HREFS)
  });
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    if (activeTab === 'handbook') loadHandbook();
  }, [activeTab]);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [portalResetEmpId, setPortalResetEmpId] = useState('');
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [exportPassword, setExportPassword] = useState('');
  const [showExportPassword, setShowExportPassword] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [wipeConfirmation, setWipeConfirmation] = useState('');
  const [wiping, setWiping] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [handbookItems, setHandbookItems] = useState([]);
  const [handbookLoading, setHandbookLoading] = useState(false);
  const [showHandbookForm, setShowHandbookForm] = useState(false);
  const [editingHandbook, setEditingHandbook] = useState(null);
  const [hbTitle, setHbTitle] = useState('');
  const [hbCategory, setHbCategory] = useState('General');
  const [hbContent, setHbContent] = useState('');
  const [hbStatus, setHbStatus] = useState('published');
  const [hbFile, setHbFile] = useState(null);
  const [savingHandbook, setSavingHandbook] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = currentUser.role === 'viewer' || currentUser.role === 'report_viewer';
  const isSuperAdmin = currentUser.role === 'super_admin';
  const canManageUsers = currentUser.permissions?.can_manage_users || isSuperAdmin;

  useEffect(() => {
    loadCompanies();
    seedCompanies();
    if (canManageUsers || currentUser.role === 'admin') {
      loadUsers();
      loadRoles();
    }
    loadAllEmployees();
  }, []);

  const loadAllEmployees = async () => {
    try {
      const res = await api.get('/api/employees?active_only=true');
      setAllEmployeesList(res.data);
    } catch { /* ignore */ }
  };

  const handlePortalPasswordReset = async () => {
    if (!portalResetEmpId) return;
    const emp = allEmployeesList.find(e => e.id === portalResetEmpId);
    if (!window.confirm(`Reset portal password for ${emp?.name || 'employee'}? Password will be set to their Employee Code.`)) return;
    try {
      const res = await api.post('/api/employee-portal/admin-reset-password', { employee_id: portalResetEmpId });
      toast({ title: 'Success', description: `Password reset to ${res.data.emp_code}` });
      setPortalResetEmpId('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to reset password', variant: 'destructive' });
    }
  };

  const handleExportData = async () => {
    if (!exportPassword || exportPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      const res = await api.post('/api/admin/export-data', { password: exportPassword }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      link.setAttribute('download', `hrms_export_${now.toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Success', description: 'Encrypted data export downloaded' });
      setExportPassword('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Export failed', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleWipeData = async () => {
    if (wipeConfirmation !== 'DELETE ALL DATA') {
      toast({ title: 'Error', description: 'Confirmation phrase does not match', variant: 'destructive' });
      return;
    }
    setWiping(true);
    try {
      const res = await api.delete('/api/admin/wipe-data', { data: { confirmation: wipeConfirmation } });
      toast({ title: 'Data Wiped', description: res.data.message });
      setShowWipeDialog(false);
      setWipeConfirmation('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Wipe failed', variant: 'destructive' });
    } finally {
      setWiping(false);
    }
  };

  const loadHandbook = async () => {
    setHandbookLoading(true);
    try {
      const res = await api.get('/api/handbook');
      setHandbookItems(res.data);
    } catch { /* silent */ }
    setHandbookLoading(false);
  };

  const openHandbookForm = (item = null) => {
    if (item) {
      setEditingHandbook(item);
      setHbTitle(item.title);
      setHbCategory(item.category);
      setHbContent(item.content || '');
      setHbStatus(item.status);
    } else {
      setEditingHandbook(null);
      setHbTitle('');
      setHbCategory('General');
      setHbContent('');
      setHbStatus('published');
    }
    setHbFile(null);
    setShowHandbookForm(true);
  };

  const handleSaveHandbook = async () => {
    if (!hbTitle.trim()) {
      toast({ title: 'Error', description: 'Title is required', variant: 'destructive' });
      return;
    }
    setSavingHandbook(true);
    try {
      const formData = new FormData();
      formData.append('title', hbTitle);
      formData.append('category', hbCategory);
      formData.append('content', hbContent);
      formData.append('status', hbStatus);
      if (hbFile) formData.append('file', hbFile);

      if (editingHandbook) {
        await api.put(`/api/handbook/${editingHandbook.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/api/handbook', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast({ title: 'Success', description: `Handbook entry ${editingHandbook ? 'updated' : 'created'}` });
      setShowHandbookForm(false);
      loadHandbook();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSavingHandbook(false);
    }
  };

  const handleDeleteHandbook = async (id) => {
    if (!window.confirm('Delete this handbook entry?')) return;
    try {
      await api.delete(`/api/handbook/${id}`);
      toast({ title: 'Deleted', description: 'Handbook entry removed' });
      loadHandbook();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const seedCompanies = async () => {
    try {
      await api.get('/api/companies/seed');
    } catch (error) {
      // Silently fail if already seeded
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load companies', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/api/users/');
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get('/api/users/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to load roles, using static roles');
      setRoles(STATIC_ROLES);
    }
  };

  const upgradeToSuperAdmin = async () => {
    try {
      await api.post('/api/users/upgrade-to-super-admin');
      toast({ title: 'Success', description: 'Admin upgraded to Super Admin. Please log out and log back in.' });
      loadUsers();
    } catch (error) {
      toast({ title: 'Info', description: error.response?.data?.detail || error.response?.data?.message || 'Already a Super Admin' });
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.code) {
      toast({ title: 'Error', description: 'Name and Code are required', variant: 'destructive' });
      return;
    }

    try {
      if (editingCompany) {
        await api.put(`/api/companies/${editingCompany.id}`, formData);
        toast({ title: 'Success', description: 'Company updated successfully' });
      } else {
        await api.post('/api/companies', formData);
        toast({ title: 'Success', description: 'Company added successfully' });
      }
      loadCompanies();
      setDialogOpen(false);
      setFormData({ name: '', code: '', description: '' });
      setEditingCompany(null);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save company', 
        variant: 'destructive' 
      });
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({ 
      name: company.name, 
      code: company.code, 
      description: company.description || '' 
    });
    setDialogOpen(true);
  };

  const handleToggleActive = async (company) => {
    try {
      await api.put(`/api/companies/${company.id}`, { is_active: !company.is_active });
      toast({ 
        title: 'Success', 
        description: `Company ${company.is_active ? 'deactivated' : 'activated'} successfully` 
      });
      loadCompanies();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update company status', variant: 'destructive' });
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${company.name}?`)) {
      return;
    }

    try {
      await api.delete(`/api/companies/${company.id}?permanent=true`);
      toast({ title: 'Success', description: 'Company deleted successfully' });
      loadCompanies();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete company', 
        variant: 'destructive' 
      });
    }
  };

  const handleMigrateEmployees = async () => {
    try {
      const response = await api.post('/api/companies/migrate-employees');
      toast({ 
        title: 'Success', 
        description: `Migrated ${response.data.modified_count} employees` 
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Migration failed', variant: 'destructive' });
    }
  };

  // User Management Functions
  const handleUserSave = async () => {
    if (!userFormData.username || (!editingUser && !userFormData.password) || !userFormData.name) {
      toast({ title: 'Error', description: 'Username, Password, and Name are required', variant: 'destructive' });
      return;
    }

    const accessPayload = userFormData.custom_access && userFormData.role !== 'super_admin' ? userFormData.module_access : null;
    try {
      if (editingUser) {
        // Check if username is being changed
        if (editingUser.username !== userFormData.username) {
          // Change username first
          await api.post(`/api/users/change-username?old_username=${encodeURIComponent(editingUser.username)}&new_username=${encodeURIComponent(userFormData.username)}`);
          toast({ title: 'Success', description: `Username changed to "${userFormData.username}"` });
        }
        
        // Update other fields
        const updateData = {
          name: userFormData.name,
          role: userFormData.role,
          email: userFormData.email || null,
          is_active: userFormData.is_active,
          module_access: accessPayload
        };
        if (userFormData.password) {
          updateData.new_password = userFormData.password;
        }
        await api.put(`/api/users/${userFormData.username}`, updateData);
        toast({ title: 'Success', description: 'User updated successfully' });
      } else {
        const { custom_access, ...createData } = userFormData;
        await api.post('/api/users/', { ...createData, module_access: accessPayload });
        toast({ title: 'Success', description: 'User created successfully' });
      }
      loadUsers();
      setUserDialogOpen(false);
      resetUserForm();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save user', 
        variant: 'destructive' 
      });
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      username: '',
      password: '',
      name: '',
      role: 'viewer',
      email: '',
      is_active: true,
      custom_access: true,
      module_access: presetForRole('viewer', ALL_HREFS)
    });
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      username: user.username,
      password: '',
      name: user.name,
      role: user.role,
      email: user.email || '',
      is_active: user.is_active !== false,
      custom_access: !!user.module_access,
      module_access: user.module_access || presetForRole(user.role, ALL_HREFS)
    });
    setUserDialogOpen(true);
  };

  const handleToggleUserActive = async (user) => {
    try {
      await api.put(`/api/users/${user.id || user.username}`, { is_active: !user.is_active });
      toast({ 
        title: 'Success', 
        description: `User ${user.is_active ? 'deactivated' : 'activated'} successfully` 
      });
      loadUsers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to update user', 
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/users/${user.id || user.username}`);
      toast({ title: 'Success', description: 'User deleted successfully' });
      loadUsers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete user', 
        variant: 'destructive' 
      });
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    try {
      await api.post('/api/users/reset-password', {
        user_id: resetPasswordUser.id || resetPasswordUser.username,
        new_password: newPassword
      });
      toast({ title: 'Success', description: `Password reset for ${resetPasswordUser.name}` });
      setShowResetDialog(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to reset password', 
        variant: 'destructive' 
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" data-testid="settings-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Settings2 className="h-8 w-8 text-purple-600" />
              Settings
            </h1>
            <p className="text-gray-600 mt-1">Manage system configuration and users</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-7 h-auto gap-1">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="leave-rules" className="flex items-center gap-2" data-testid="leave-rules-tab-trigger">
              <CalendarDays className="h-4 w-4" />
              Leave Rules
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Tools
            </TabsTrigger>
            <TabsTrigger value="handbook" className="flex items-center gap-2" data-testid="handbook-tab-trigger">
              <BookOpen className="h-4 w-4" />
              Handbook
            </TabsTrigger>
            <TabsTrigger value="view-windows" className="flex items-center gap-2" data-testid="view-windows-tab-trigger">
              <Eye className="h-4 w-4" />
              View Windows
            </TabsTrigger>
            <TabsTrigger value="esc" className="flex items-center gap-2" data-testid="esc-settings-tab-trigger">
              <BadgeCheck className="h-4 w-4" />
              ESC
            </TabsTrigger>
            <TabsTrigger value="operations" className="flex items-center gap-2" data-testid="operations-settings-tab-trigger">
              <Bell className="h-4 w-4" />
              Operations
            </TabsTrigger>
            <TabsTrigger value="hygiene" className="flex items-center gap-2" data-testid="hygiene-settings-tab-trigger">
              <ShieldAlert className="h-4 w-4" />
              Data Hygiene
            </TabsTrigger>
            <TabsTrigger value="hr-report" className="flex items-center gap-2" data-testid="hr-report-settings-tab-trigger">
              <FileBarChart className="h-4 w-4" />
              HR Report
            </TabsTrigger>
            <TabsTrigger value="wps" className="flex items-center gap-2" data-testid="wps-settings-tab-trigger">
              <Banknote className="h-4 w-4" />
              WPS / SIF
            </TabsTrigger>
            <TabsTrigger value="auto-reports" className="flex items-center gap-2" data-testid="auto-reports-tab-trigger">
              <CalendarClock className="h-4 w-4" />
              Auto Reports
            </TabsTrigger>
            <TabsTrigger value="menu-order" className="flex items-center gap-2" data-testid="menu-order-tab-trigger">
              <Settings2 className="h-4 w-4" />
              Menu Order
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="exclusions" className="flex items-center gap-2" data-testid="exclusions-tab-trigger">
                <ShieldAlert className="h-4 w-4" />
                Exclusions
              </TabsTrigger>
            )}
          </TabsList>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-600" />
                      User Management
                    </CardTitle>
                    <CardDescription>
                      Manage user accounts and access levels
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {!isSuperAdmin && currentUser.role === 'admin' && (
                      <Button 
                        variant="outline" 
                        onClick={upgradeToSuperAdmin}
                        className="text-red-600 border-red-200"
                        data-testid="upgrade-super-admin-btn"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Become Super Admin
                      </Button>
                    )}
                    {canManageUsers && (
                      <>
                        <Button variant="outline" onClick={loadUsers}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh
                        </Button>
                        <Dialog open={userDialogOpen} onOpenChange={(open) => {
                          setUserDialogOpen(open);
                          if (!open) setTimeout(resetUserForm, 200);
                        }}>
                          <DialogTrigger asChild>
                            <Button data-testid="add-user-btn">
                              <UserPlus className="h-4 w-4 mr-2" />
                              Add User
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>
                                {editingUser ? 'Edit User' : 'Add New User'}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                              <div>
                                <Label>Username *</Label>
                                <Input 
                                  value={userFormData.username}
                                  onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                                  placeholder="e.g., john.doe"
                                  data-testid="user-username-input"
                                />
                                {editingUser && editingUser.username !== userFormData.username && (
                                  <p className="text-xs text-amber-600 mt-1">Username will be changed from "{editingUser.username}" to "{userFormData.username}"</p>
                                )}
                              </div>
                              <div>
                                <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
                                <div className="relative">
                                  <Input 
                                    type={showPassword ? 'text' : 'password'}
                                    value={userFormData.password}
                                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                                    placeholder={editingUser ? '••••••••' : 'Enter password'}
                                    data-testid="user-password-input"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                  >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <Label>Display Name *</Label>
                                <Input 
                                  value={userFormData.name}
                                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                                  placeholder="e.g., John Doe"
                                  data-testid="user-name-input"
                                />
                              </div>
                              <div>
                                <Label>Email</Label>
                                <Input 
                                  type="email"
                                  value={userFormData.email}
                                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                                  placeholder="e.g., john@company.com"
                                  data-testid="user-email-input"
                                />
                              </div>
                              <div>
                                <Label>Role</Label>
                                <Select 
                                  value={userFormData.role} 
                                  onValueChange={(value) => setUserFormData({ ...userFormData, role: value, module_access: presetForRole(value, ALL_HREFS) })}
                                >
                                  <SelectTrigger data-testid="user-role-select">
                                    <SelectValue placeholder="Select role">
                                      {userFormData.role && ROLE_LABELS[userFormData.role]}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(STATIC_ROLES).map(([roleKey, roleInfo]) => (
                                      <SelectItem key={roleKey} value={roleKey}>
                                        {ROLE_LABELS[roleKey]} - {roleInfo.description}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {userFormData.role !== 'super_admin' && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <Label>Custom module access</Label>
                                      <p className="text-xs text-gray-500">{userFormData.custom_access ? 'Tick what this person can see (View) and change (Edit).' : 'Off — full access allowed by the selected role.'}</p>
                                    </div>
                                    <Switch
                                      checked={userFormData.custom_access}
                                      onCheckedChange={(checked) => setUserFormData({ ...userFormData, custom_access: checked })}
                                      data-testid="user-custom-access-switch"
                                    />
                                  </div>
                                  {userFormData.custom_access && (
                                    <ModuleAccessPicker
                                      value={userFormData.module_access}
                                      role={userFormData.role}
                                      onChange={(m) => setUserFormData((f) => ({ ...f, module_access: m }))}
                                    />
                                  )}
                                </div>
                              )}
                              <div className="flex items-center justify-between">
                                <Label>Active</Label>
                                <Switch
                                  checked={userFormData.is_active}
                                  onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_active: checked })}
                                  data-testid="user-active-switch"
                                />
                              </div>
                              <div className="flex justify-end space-x-2 pt-4">
                                <Button 
                                  variant="outline" 
                                  onClick={() => {
                                    setUserDialogOpen(false);
                                    setTimeout(resetUserForm, 200);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={handleUserSave}
                                  data-testid="save-user-btn"
                                >
                                  {editingUser ? 'Update' : 'Create'} User
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!canManageUsers && currentUser.role !== 'admin' ? (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>You don't have permission to manage users</p>
                    <p className="text-sm">Contact Super Admin for access</p>
                  </div>
                ) : usersLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : users.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No users found</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((user) => (
                      <div 
                        key={user.id || user.username} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          user.is_active !== false ? 'bg-white' : 'bg-gray-50 opacity-60'
                        }`}
                        data-testid={`user-row-${user.username}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            user.role === 'super_admin' ? 'bg-red-100' : 
                            user.role === 'admin' ? 'bg-purple-100' : 'bg-gray-100'
                          }`}>
                            {user.role === 'super_admin' ? (
                              <Shield className="h-6 w-6 text-red-600" />
                            ) : (
                              <Users className="h-6 w-6 text-purple-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{user.name}</h3>
                              <Badge className={`${ROLE_COLORS[user.role] || 'bg-gray-100'} border`}>
                                {ROLE_LABELS[user.role] || user.role}
                              </Badge>
                              {user.is_active === false && (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                              {user.module_access && user.role !== 'super_admin' && (
                                <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200" data-testid={`user-custom-access-badge-${user.username}`}>
                                  Custom access · {Object.keys(user.module_access).length} modules
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">@{user.username}</p>
                            {user.email && (
                              <p className="text-xs text-gray-400">{user.email}</p>
                            )}
                          </div>
                        </div>
                        {canManageUsers && user.username !== currentUser.username && (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setResetPasswordUser(user);
                                setShowResetDialog(true);
                              }}
                              title="Reset Password"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleToggleUserActive(user)}
                              title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active !== false ? (
                                <X className="h-4 w-4 text-orange-600" />
                              ) : (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleEditUser(user)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {user.username === currentUser.username && (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            You
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Role Permissions Guide */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-3">Role Permissions Guide</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(ROLE_LABELS).map(([roleKey, roleLabel]) => (
                      <div key={roleKey} className="text-sm">
                        <Badge className={`${ROLE_COLORS[roleKey]} border mb-1`}>
                          {roleLabel}
                        </Badge>
                        <p className="text-xs text-gray-500">
                          {roles[roleKey]?.description || STATIC_ROLES[roleKey]?.description || ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reset Password Dialog */}
                <Dialog open={showResetDialog} onOpenChange={(open) => {
                  setShowResetDialog(open);
                  if (!open) {
                    setResetPasswordUser(null);
                    setNewPassword('');
                  }
                }}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Key className="h-5 w-5 text-blue-600" />
                        Reset Password
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <p className="text-sm text-gray-600">
                        Reset password for <strong>{resetPasswordUser?.name}</strong> (@{resetPasswordUser?.username})
                      </p>
                      <div>
                        <Label>New Password</Label>
                        <div className="relative">
                          <Input 
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min 6 characters)"
                            data-testid="reset-password-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowResetDialog(false);
                            setResetPasswordUser(null);
                            setNewPassword('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleResetPassword}
                          data-testid="confirm-reset-password-btn"
                        >
                          Reset Password
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leave Rules Tab */}
          <TabsContent value="leave-rules">
            <LeaveRulesTab isViewer={isViewer} />
            <WelcomeBackSettingsCard readOnly={isViewer} />
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <CompanyProfileCard readOnly={isViewer} />
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      Companies Management
                    </CardTitle>
                    <CardDescription>
                      Manage companies for payment method allocation
                    </CardDescription>
                  </div>
                  {!isViewer && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          onClick={() => {
                            setEditingCompany(null);
                            setFormData({ name: '', code: '', description: '' });
                          }}
                          data-testid="add-company-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Company
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {editingCompany ? 'Edit Company' : 'Add New Company'}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label>Company Name *</Label>
                            <Input 
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder="e.g., MSBR Holdings"
                              data-testid="company-name-input"
                            />
                          </div>
                          <div>
                            <Label>Company Code *</Label>
                            <Input 
                              value={formData.code}
                              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                              placeholder="e.g., MSBR"
                              data-testid="company-code-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Short code used for identification
                            </p>
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea 
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              placeholder="Optional description"
                              data-testid="company-description-input"
                            />
                          </div>
                          <div className="flex justify-end space-x-2 pt-4">
                            <Button 
                              variant="outline" 
                              onClick={() => setDialogOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleSave}
                              data-testid="save-company-btn"
                            >
                              {editingCompany ? 'Update' : 'Add'} Company
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No companies found</p>
                ) : (
                  <div className="space-y-3">
                    {companies.map((company) => (
                      <div 
                        key={company.id} 
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          company.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
                        }`}
                        data-testid={`company-row-${company.code}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-purple-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{company.name}</h3>
                              <Badge variant="outline">{company.code}</Badge>
                              {!company.is_active && (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </div>
                            {company.description && (
                              <p className="text-sm text-gray-500">{company.description}</p>
                            )}
                          </div>
                        </div>
                        {!isViewer && (
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleToggleActive(company)}
                              title={company.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {company.is_active ? (
                                <X className="h-4 w-4 text-orange-600" />
                              ) : (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleEdit(company)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDelete(company)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-4">
            {!isViewer && (
              <>
                {/* Employee Portal Password Reset */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="h-4 w-4" /> Employee Portal - Reset Password
                    </CardTitle>
                    <CardDescription>
                      Reset an employee's portal password to their Employee Code
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label className="text-sm">Select Employee</Label>
                        <Select 
                          value={portalResetEmpId || ''} 
                          onValueChange={setPortalResetEmpId}
                        >
                          <SelectTrigger className="mt-1" data-testid="portal-reset-employee-select">
                            <SelectValue placeholder="Select employee..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allEmployeesList.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.employee_code} - {emp.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={handlePortalPasswordReset}
                        disabled={!portalResetEmpId}
                        data-testid="portal-reset-password-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" /> Reset Password
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Password will be reset to the employee's code. They will be prompted to change it on next login.
                    </p>
                  </CardContent>
                </Card>

                {/* Data Migration */}
                <Card>
                  <CardHeader>
                    <CardTitle>Data Migration</CardTitle>
                    <CardDescription>
                      One-time migration tools for existing data
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div>
                        <h4 className="font-medium text-yellow-800">Migrate Employee Payment Methods</h4>
                        <p className="text-sm text-yellow-600">
                          Set payment_method from visa_company for employees who don't have it set
                        </p>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={handleMigrateEmployees}
                        data-testid="migrate-employees-btn"
                      >
                        Run Migration
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Download All Data (Encrypted) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="h-4 w-4 text-blue-600" /> Download All Data (Encrypted)
                    </CardTitle>
                    <CardDescription>
                      Export all employees, leaves, payroll, and more as an AES-256 encrypted ZIP file
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <Label className="text-sm">Encryption Password</Label>
                        <div className="relative mt-1">
                          <Input
                            type={showExportPassword ? 'text' : 'password'}
                            value={exportPassword}
                            onChange={(e) => setExportPassword(e.target.value)}
                            placeholder="Min 6 characters"
                            data-testid="export-password-input"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowExportPassword(!showExportPassword)}
                          >
                            {showExportPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      <Button
                        onClick={handleExportData}
                        disabled={!exportPassword || exportPassword.length < 6 || exporting}
                        data-testid="export-data-btn"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {exporting ? 'Exporting...' : 'Download Export'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      The ZIP file is protected with AES-256 encryption. You will need this password to open it. Keep it safe.
                    </p>
                  </CardContent>
                </Card>

                {/* Wipe All Data */}
                {isSuperAdmin && (
                  <Card className="border-red-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="h-4 w-4" /> Wipe All Data
                      </CardTitle>
                      <CardDescription className="text-red-500">
                        Permanently delete all employees, leaves, payroll, and system data. This action cannot be undone.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
                        <p className="text-sm text-red-700 font-medium">
                          Warning: This will permanently delete ALL data from every collection in the database. 
                          Only system users and companies will be preserved.
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={() => setShowWipeDialog(true)}
                        data-testid="wipe-data-trigger-btn"
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" /> Wipe All Data
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Wipe Confirmation Dialog */}
            <Dialog open={showWipeDialog} onOpenChange={(open) => {
              setShowWipeDialog(open);
              if (!open) setWipeConfirmation('');
            }}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" /> Confirm Data Wipe
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700">
                      You are about to <strong>permanently delete all data</strong> including employees, leaves, payroll records, attendance, KPI reviews, assets, and portal data.
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm">Type <strong className="text-red-600">DELETE ALL DATA</strong> to confirm</Label>
                    <Input
                      value={wipeConfirmation}
                      onChange={(e) => setWipeConfirmation(e.target.value)}
                      placeholder="DELETE ALL DATA"
                      className="mt-1 border-red-200 focus:ring-red-500"
                      data-testid="wipe-confirmation-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => { setShowWipeDialog(false); setWipeConfirmation(''); }}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleWipeData}
                      disabled={wipeConfirmation !== 'DELETE ALL DATA' || wiping}
                      data-testid="confirm-wipe-btn"
                    >
                      {wiping ? 'Wiping...' : 'Permanently Delete All Data'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Handbook Management Tab */}
          <TabsContent value="handbook" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-indigo-600" /> Company Handbook
                    </CardTitle>
                    <CardDescription>Manage policies, guidelines, and documents for employees</CardDescription>
                  </div>
                  <Button onClick={() => openHandbookForm()} data-testid="add-handbook-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Entry
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!handbookLoading && handbookItems.length === 0 && (
                  <div className="text-center py-8">
                    <BookOpen className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                    <p className="text-sm text-gray-400">No handbook entries yet. Click "Add Entry" to create one.</p>
                    <Button variant="outline" className="mt-3" onClick={loadHandbook}>
                      <RefreshCw className="h-4 w-4 mr-1" /> Load Entries
                    </Button>
                  </div>
                )}
                {handbookLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {handbookItems.map(item => (
                      <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50" data-testid={`hb-item-${item.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                            <Badge className={`text-[10px] ${item.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                              {item.status}
                            </Badge>
                            <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                          </div>
                          {item.content && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.content}</p>}
                          <div className="flex items-center gap-3 mt-1.5">
                            {item.file_name && (
                              <span className="text-[10px] text-indigo-500 flex items-center gap-0.5">
                                <FileTextIcon className="h-3 w-3" /> {item.file_name}
                              </span>
                            )}
                            <span className="text-[10px] text-gray-300">
                              {new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            <span className="text-[10px] text-gray-400">{item.read_count || 0} reads</span>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button size="sm" variant="ghost" onClick={() => openHandbookForm(item)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteHandbook(item.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Handbook Form Dialog */}
            <Dialog open={showHandbookForm} onOpenChange={(open) => {
              setShowHandbookForm(open);
              if (!open) { setEditingHandbook(null); setHbFile(null); }
            }}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingHandbook ? 'Edit' : 'Add'} Handbook Entry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>Title *</Label>
                    <Input value={hbTitle} onChange={e => setHbTitle(e.target.value)} className="mt-1" data-testid="hb-title-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Category</Label>
                      <Select value={hbCategory} onValueChange={setHbCategory}>
                        <SelectTrigger className="mt-1" data-testid="hb-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['General','Policy','HR','Safety','Finance','Operations','Training'].map(c =>
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={hbStatus} onValueChange={setHbStatus}>
                        <SelectTrigger className="mt-1" data-testid="hb-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea value={hbContent} onChange={e => setHbContent(e.target.value)} rows={4} className="mt-1" data-testid="hb-content-input" placeholder="Policy text, guidelines, or description..." />
                  </div>
                  <div>
                    <Label>File Attachment (optional)</Label>
                    <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center hover:border-indigo-300 transition cursor-pointer"
                      onClick={() => document.getElementById('hb-file-input').click()}>
                      <Upload className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                      <p className="text-xs text-gray-400">{hbFile ? hbFile.name : 'Click to upload PDF, Word, or any document'}</p>
                      <input type="file" id="hb-file-input" className="hidden" onChange={e => setHbFile(e.target.files[0])} data-testid="hb-file-input" />
                    </div>
                    {editingHandbook?.file_name && !hbFile && (
                      <p className="text-xs text-indigo-500 mt-1">Current: {editingHandbook.file_name}</p>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowHandbookForm(false)}>Cancel</Button>
                    <Button onClick={handleSaveHandbook} disabled={savingHandbook} data-testid="hb-save-btn">
                      {savingHandbook ? 'Saving...' : editingHandbook ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* View Windows Tab */}
          <TabsContent value="view-windows">
            <ViewWindowsTab isViewer={isViewer} />
          </TabsContent>

          {/* ESC Accreditation Settings */}
          <TabsContent value="esc">
            <EscSettingsTab isViewer={isViewer} />
          </TabsContent>

          {/* Operations: HR Digest recipients + Attendance thresholds */}
          <TabsContent value="operations">
            <OperationsSettingsTab isViewer={isViewer} />
          </TabsContent>

          {/* Data Hygiene: duplicate detection for leaves + employees */}
          <TabsContent value="hygiene">
            <DataHygieneTab />
          </TabsContent>

          {/* HR Report: configurable recency windows */}
          <TabsContent value="hr-report">
            <HRReportSettingsTab isViewer={isViewer} />
          </TabsContent>
          <TabsContent value="wps">
            <WpsSettingsTab isViewer={isViewer} />
          </TabsContent>
          <TabsContent value="auto-reports">
            <ScheduledReportsTab isViewer={isViewer} />
          </TabsContent>
          <TabsContent value="menu-order">
            <MenuOrderTab />
          </TabsContent>
          {isSuperAdmin && (
            <TabsContent value="exclusions">
              <ExclusionsTab isSuperAdmin={isSuperAdmin} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
